import type { FieldFunction } from '@broker-as/acquisit-ui-vue/expressions'
import { noExtractScope, noExtractUID, noRenameUID, registerFieldFunction } from '@broker-as/acquisit-ui-vue/expressions'
import { deepValue, forEachObject, isset } from '@broker-as/acquisit-ui-vue/functions'
import { DateTimeExt, useLogger } from '@broker-as/acquisit-ui-vue/library'
import type { ProtocolStoreState } from '@/stores/protocol'
import { useProtocolStore } from '@/stores/protocol'

const log = useLogger('lib/expressions/field-functions', useLogger.COLOR_LIB)

const createProtocolStoreAccessor = <I = any, E = any>(property: string, transform?: (val: I) => E): FieldFunction => ({
	evaluate() {
		const val = useProtocolStore()[property]
		
		if (transform) {
			return transform(val)
		}
		
		return val
	},
	
	extractUIDs: noExtractUID,
	renameUID: noRenameUID,
	extractScopes: noExtractScope,
})

/**
 * OFFICE() = office object
 * OFFICE(property) = office's property, e.g. OFFICE("id")
 */
export const OFFICE: FieldFunction = {
	evaluate(args, _) {
		const protocolStore = useProtocolStore()
		return deepValue(protocolStore.office ?? {}, args)
	},
	
	extractUIDs: noExtractUID,
	renameUID: noRenameUID,
	extractScopes: noExtractScope,
}

/**
 * IMPORTS(uid) = All values for the import of that UID
 */
export const IMPORTS: FieldFunction = {
	evaluate(args, _) {
		// First argument must be the UID of the import
		if (args.length === 1) {
			const protocolStore = useProtocolStore()
			
			for (const imp of protocolStore.imports) {
				if (imp.uid === args[0]) {
					if (!Array.isArray(imp.value) && typeof(imp.value) !== 'object' && isset(imp.value)) {
						return [ imp.value ]
					}
					
					return imp.value
				}
			}
			
			return []
		} else {
			log.always.warnFrom('IMPORTS', 'Invalid arguments:', args)
			log.always.warnFrom('IMPORTS', 'Expected arguments: string')
		}
		
		return undefined
	},
	
	extractUIDs: noExtractUID,
	renameUID: noRenameUID,
	extractScopes: noExtractScope
}

/**
 * TASK_TYPE() = the protocol's task type
 */
export const TASK_TYPE: FieldFunction = createProtocolStoreAccessor('task_type')

/**
 * PROPERTY_TYPE() = the property's task type
 */
export const PROPERTY_TYPE: FieldFunction = createProtocolStoreAccessor('property_type')

/**
 * TAKEOVER_DATE() = the takeover date of the estate
 * Use DATE_* functions to work with this date
 */
export const TAKEOVER_DATE: FieldFunction = createProtocolStoreAccessor<ProtocolStoreState['takeover_date']>('takeover_date', (date) => {
	if (date === undefined || date === null) {
		return null
	}
	
	return DateTimeExt.fromTimestamp(date)
})

export const FUNCTIONS: Record<string, FieldFunction> = {
	OFFICE,
	TASK_TYPE,
	PROPERTY_TYPE,
	TAKEOVER_DATE,
	IMPORTS
}

export const registerCustomFieldFunctions = () => {
	forEachObject(FUNCTIONS, (fn, name) => {
		registerFieldFunction(name, fn)
	})
}